import {
  MoreVert as MoreIcon,
  Refresh as ResetIcon,
} from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import * as labels from "config.labels";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programPricingStore from "store/programPricing";

const ActionMenu = ({ resetCoverages }) => {
  const anchorRef = useRef(null);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={"menu"}>
      <IconButton
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        variant={"text"}
        className={"toggle"}
        style={{ marginLeft: "-15px" }}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={(_, reason) => {
          if (reason === "backdropClick") setIsOpen(!isOpen);
        }}
      >
        <MenuItem
          onClick={() => {
            resetCoverages();
            setIsOpen(false);
          }}
        >
          {" "}
          <ListItemIcon>
            <ResetIcon />
          </ListItemIcon>
          <ListItemText>
            {translateLabel(labels.BINARY_COVERAGES_RESET_TO_LATEST, {
              default: "Reset to latest coverages list",
            })}
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

const BinaryCoverages = () => {
  const dispatch = useDispatch();

  const programPricing = useSelector(programPricingStore.select);

  const { data: submissionDefaults } = staticDataService.useConfigQuery(
    "submission_defaults"
  );

  const binaryCoverages = programPricing?.coverageConfig?.binaryCoverages ?? [];

  const toggleCoverage = ({ key, checked }) => {
    dispatch(
      programPricingStore.update({
        binaryCoverages: {
          ...programPricing?.binaryCoverages,
          [key]: checked,
        },
      })
    );
  };

  const selectAllCoverages = () => {
    const allSelected = {};
    binaryCoverages.forEach(({ key }) => {
      allSelected[key] = true;
    });

    dispatch(
      programPricingStore.update({
        binaryCoverages: {
          ...programPricing?.binaryCoverages,
          ...allSelected,
        },
      })
    );
  };

  const deselectAllCoverages = () => {
    const allDeselected = {};
    binaryCoverages.forEach(({ key }) => {
      allDeselected[key] = false;
    });

    dispatch(
      programPricingStore.update({
        binaryCoverages: {
          ...programPricing?.binaryCoverages,
          ...allDeselected,
        },
      })
    );
  };

  const resetCoverages = () => {
    dispatch(
      programPricingStore.update({
        coverageConfig:
          submissionDefaults?.programPricing?.coverageConfig ?? {},
      })
    );
  };

  return (
    <Component
      title={"Coverages"}
      options={
        <Stack direction={"row"} spacing={2}>
          <Disabled ifReadOnly>
            <Link
              component={"button"}
              variant={"body2"}
              color={"secondary"}
              onClick={selectAllCoverages}
              sx={{ display: "block", marginBottom: "8px" }}
            >
              {"Select All"}
            </Link>
          </Disabled>
          <Disabled ifReadOnly>
            <Link
              component={"button"}
              variant={"body2"}
              color={"secondary"}
              onClick={deselectAllCoverages}
              sx={{ display: "block", marginBottom: "8px" }}
            >
              {"Deselect All"}
            </Link>
          </Disabled>
          <ActionMenu resetCoverages={resetCoverages} />
        </Stack>
      }
    >
      <Disabled ifReadOnly>
        <Stack direction={"row"} useFlexGap flexWrap={"wrap"} gap={2}>
          {binaryCoverages.map(({ key, displayName }) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={programPricing?.binaryCoverages?.[key] ?? false}
                  onChange={(e) =>
                    toggleCoverage({ key, checked: e.target.checked })
                  }
                  color={"primary"}
                />
              }
              label={displayName}
              labelPlacement={"top"}
            />
          ))}
        </Stack>
      </Disabled>
    </Component>
  );
};

export default BinaryCoverages;
